import { IVehicle } from 'data/schema';
import { IPublicSession } from 'state/session';
import { user } from 'utils/auth/user';
import { State } from 'utils/state';
import { tenantConfig } from 'utils/tenant';
import { IntegrationType, integrationType } from 'utils/ui/app';
import { EExperience } from 'data/schema';

/**
 * Requirements that must be met before a session is ready for sharing
 */
interface ISessionReadyRequirements {
  // real-time syncing of session data is initialized
  broadcastingEstablished: boolean;

  // session state is saved to the server or successfully loaded from it (e.g. on reload)
  stateSynced: boolean;
}

/**
 * Keys that can be used to trigger session requirements
 */
type TSessionRequirementKey = keyof ISessionReadyRequirements;

/**
 * Keywords:
 * - session: a session in any state (started, in progress, ended, etc.). It has an ID, but nothing else guaranteed.
 * - presentation or experience: session that is in progress, customer is allowed to join, and all pre-requisites for the customer to see a 3D stream are met.
 *
 */
export class SessionState extends State<IPublicSession> {
  /**
   * Flags that match ISessionReadyRequirements will have to be set to true to confirm that they are ready
   */
  private $sessionReadyFlags: Partial<ISessionReadyRequirements> = {};

  markRequirementReady(requirement: TSessionRequirementKey): void {
    this.$sessionReadyFlags[requirement] = true;
  }

  /**
   * Experiences, e.g. 2D can only be started when these requirements are met
   */
  get sessionReadyForExperience(): boolean {
    return !!this.get('prString') && !!this.$sessionReadyFlags['stateSynced'];
  }

  /**
   * Showroom streaming can start only when these requirements are met
   */
  get sessionReadyForStreaming(): boolean {
    return this.sessionReadyForExperience && !!this.$sessionReadyFlags['broadcastingEstablished'];
  }

  get hasStreamingId(): boolean {
    return !!this.get('aveSessionId') || !!this.get('aveChannelName');
  }

  get isLocalAveSession(): boolean {
    return this.get('sharingType') === 'broadcast-local' && this.get('isShared') && !!this.get('streamingDevice');
  }

  get isConnectedToLocalAve(): boolean {
    return this.isLocalAveSession && !!this.get('aveChannelName');
  }

  get voipEnabled(): boolean {
    return this.get('sharingType') === 'remote' && tenantConfig().isAllowed('voip');
  }

  get presentationStarted(): boolean {
    const session = this.get();
    return Boolean(
      (session.activeExperience === '2D' && session.carImagesStatus === 'ready') || this.showroomConnected
    );
  }

  // This is a brief period of time while the showroom is connecting, but not yet connected. aveSessionId not yet available
  get showroomConnecting(): boolean {
    const session = this.get();
    return Boolean(session.activeExperience === '3D' && !this.hasStreamingId && session.streamActive);
  }

  get showroomConnected(): boolean {
    const session = this.get();
    return Boolean(session.activeExperience === '3D' && this.hasStreamingId && session.streamActive);
  }

  get canDownload3dSnapshot(): boolean {
    return tenantConfig().isAllowed('download3dSnapshot') && this.showroomConnected;
  }

  get canDownload2dImage(): boolean {
    return (
      tenantConfig().isAllowed('download2dImage') &&
      tenantConfig().isAllowed('2D') &&
      this.get('activeExperience') === '2D' &&
      this.get('comparisonMode') !== 'on'
    );
  }

  get canDownloadComparisonImage(): boolean {
    return (
      tenantConfig().isAllowed('downloadComparisonImage') &&
      tenantConfig().isAllowed('2D') &&
      this.get('comparisonMode') === 'on'
    );
  }

  /**
   * Whether any download feature flag is enabled in tenant config.
   * Unlike the `can*` getters, this only checks raw flags — not runtime conditions
   * like active experience or stream connectivity.
   */
  get hasAnyDownloadFlag(): boolean {
    const tc = tenantConfig();
    return (
      tc.isAllowed('download2dImage') || tc.isAllowed('download3dSnapshot') || tc.isAllowed('downloadComparisonImage')
    );
  }

  /**
   * Determines if the sharing dashboard button can be opened.
   *
   * This is a simple check for UI button enabling:
   * - Must have a session
   * - Car images must not be loading
   * - If 3D is active but not connected and 2D is disabled, cannot open
   */
  get canOpenSharingDashboard(): boolean {
    const session = this.get();

    if (!session.id) return false;
    if (session.carImagesStatus === 'loading') return false;

    // If 3D is active but not connected and 2D is disabled, cannot share (no fallback)
    if (session.activeExperience === '3D' && !this.showroomConnected && !tenantConfig().isAllowed('2D')) {
      return false;
    }

    return true;
  }

  /**
   * Determines if sharing can be started.
   *
   * This checks all conditions needed to actually start a sharing session:
   * - Session must be ready (has ID and prString)
   * - Different requirements based on sharing type and whether streaming is required
   */
  get canStartSharing(): boolean {
    const session = this.get();

    // Must be ready for sharing (has ID and prString)
    if (!session.id || !session.prString) {
      return false;
    }

    const hasStreamingDevice = !!session.streamingDevice;
    const isLocalRenderingAvailable = tenantConfig().isAllowed('sharingBroadcastLocal');
    const streamIsRequired = !isLocalRenderingAvailable;

    if (streamIsRequired) {
      const isCarReady = session.carStatus === 'ready';
      if (session.sharingType === 'broadcast') {
        return isCarReady && hasStreamingDevice;
      }
      return isCarReady;
    }

    if (session.sharingType === 'broadcast-local') {
      return hasStreamingDevice;
    }

    return true;
  }

  get customerCanAccessPresentation(): boolean {
    return (
      this.get('customerAllowed') &&
      ((this.get('activeExperience') === '3D' && this.get('streamActive')) || this.get('activeExperience') === '2D')
    );
  }

  /**
   * If it is a broadcast or a device session, we don't want to show the hangup button in the customer view
   */
  get customerCanSeeHangupButton(): boolean {
    return !(
      user.type === 'customer' &&
      (this.get('sharingType') === 'broadcast' || this.get('sharingType') === 'device')
    );
  }

  get sessionEnded(): boolean {
    return !!this.get('endedAt') && !this.get('streamActive');
  }

  /**
   * Session has once started, shared and ended
   */
  get presentationCompleted(): boolean {
    return this.sessionEnded && this.get('isShared');
  }

  /**
   * Session was never shared when it was ended
   * it has ended, but never shared. However, if scheduled time is added then its not abandoned, but delayed
   */
  get sessionAbandoned(): boolean {
    return !!this.get('endedAt') && !this.get('isShared') && !this.get('scheduledStartTime'); // no rating for this session
  }

  /**
   * Session is scheduled and hasn't started yet
   */
  get sessionScheduled(): boolean {
    return !this.get('endedAt') && !this.get('isShared') && !!this.get('scheduledStartTime');
  }

  /**
   * Session is started and then ended, but rating is not completed
   */
  get presentationEndedWithoutRating(): boolean {
    return Boolean(this.get('id') && !this.get('ratingCompleted'));
  }

  get sessionEligibleForRating(): boolean {
    const isDealer = user.type === 'dealer';
    const isCustomer = user.type === 'customer';

    // [AVS-3728] Nerd sessions should not have a rating modal for customers
    const isBroadcastSession = this.get('sharingType') === 'broadcast';
    const isCustomerAndNotBroadcast = isCustomer && !isBroadcastSession;

    // For dealers, the session must have ended and there should not be a rating already
    const isDealerAndPresentationEnded = isDealer && this.presentationEndedWithoutRating;

    return isCustomerAndNotBroadcast || isDealerAndPresentationEnded;
  }

  get dealerWaitingRoomEligible() {
    return (
      !this.dealerReadyToPresent &&
      this.get('isShared') &&
      this.get('sharingType') === 'remote' &&
      !this.presentationCompleted
    );
  }

  get dealerReadyToPresent() {
    return tenantConfig().isAllowed('voip') ? this.get('customerAllowed') : true;
  }

  get isSessionDgaEligible() {
    // [AVS-3845] DGA shows even if the dealer ends stream that was never shared
    return (
      // okapi/vin/marketing code exists
      !!this.get('vinOrMarketingCode') &&
      // sharingDga is enabled in tenant config
      tenantConfig().isAllowed('sharingDga') &&
      // the user is a dealer
      user.type === 'dealer'
    );
  }

  /**
   * DGA can be created, but hasn't been created yet
   */
  get dgaPending() {
    return Boolean(this.isSessionDgaEligible && !this.get('dgaLink'));
  }

  get dgaCreated() {
    return Boolean(this.isSessionDgaEligible && !!this.get('dgaLink'));
  }

  /**
   * Determines whether the end session button should be enabled.
   *
   * The button is enabled when the car status is 'ready' AND either:
   * - The application is running as a web component, OR
   * - The application is standalone but not currently on the goodbye page
   *
   * The function is agnostic of React Router because RR cannot be used in web components.
   *
   * @returns True if the end session button should be enabled, false otherwise
   */
  get endSessionButtonEnabled() {
    const isCarReady = this.get('carStatus') === 'ready';
    const isWebComponent = integrationType() === IntegrationType.WebComponent;
    const isOnGoodbyePage = globalThis.location.pathname.endsWith('/goodbye');
    // customer accessing sessions that are scheduled, but not yet started should not see the end session button
    const isScheduledForCustomer = this.sessionScheduled && user.type === 'customer';

    // Show button if car is ready AND (it's a web component OR standalone, but not on goodbye page)
    return isCarReady && (isWebComponent || !isOnGoodbyePage) && !isScheduledForCustomer;
  }
  get currentVehicle(): IVehicle | undefined {
    const vehicles = this.get('vehicles');
    const activeVehicleId = this.get('activeVehicleId');
    if (!vehicles || activeVehicleId == null) {
      return undefined;
    }
    return vehicles[activeVehicleId];
  }

  /**
   * Get the selected experience (2D/3D) based on session state and tenant config.
   *
   * Business Rules:
   * 1. Forces 3D when car images aren't ready or 2D is disabled
   * 2. Forces 2D when comparison mode is enabled
   * 3. Otherwise uses the default experience from config
   */
  get selectedExperience(): EExperience {
    const { config } = tenantConfig();
    const session = this.get();
    const isCarReady = session.carStatus === 'ready';

    // Rule 2: When comparison mode is enabled, force 2D if allowed
    if (session.comparisonMode === 'on' && tenantConfig().isAllowed('2D')) {
      return '2D';
    }

    // Rule 1: When no images are loaded or 2D is disabled, force 3D
    if ((session.carImagesStatus !== 'ready' || !tenantConfig().isAllowed('2D')) && isCarReady) {
      return '3D';
    }

    // Rule 3: Otherwise use default from config
    return config.defaultExperience;
  }
}
